import { useEffect, useRef, useState } from 'react'
import { getInbox, type InboxItem } from '@/services/listingsService'
import { getDemoInbox } from '@/pages/dashboardDemo'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { filterLeadsByQuery } from '@/lib/leadSearchFilter'

/**
 * useLeadSearch — MOTOR DE BÚSQUEDA COMPARTIDO de leads.
 *
 * Llama a `GET /api/v1/listings/inbox?q=…` (motor MULTICAMPO del backend: busca
 * en título, categoría, dirección, ciudad, región, email y dominio) con:
 *   · DEBOUNCE (~280 ms) sobre la query para no pegar al backend en cada tecla.
 *   · Cancelación de peticiones obsoletas (AbortController): si el usuario sigue
 *     escribiendo, la respuesta anterior se descarta y nunca pisa a la última.
 *
 * Devuelve `{ results, loading, query }`. Lo usa el command palette (⌘K); la
 * tabla de Búsqueda comparte el MISMO debounce ({@link useDebouncedValue}) pero
 * filtra a través de su propio controlador de bandeja (mismo `q`, sin duplicar
 * la lógica de fetch).
 *
 * Bajo `?preview` (bucle de fidelidad, sin backend) filtra el inbox demo en
 * local con el mismo criterio multicampo para que las capturas funcionen.
 */
const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

export interface LeadSearchResult {
  /** Resultados de la última búsqueda aplicada (vacío si la query está vacía). */
  results: InboxItem[]
  /** Hay una búsqueda en vuelo o el debounce aún no ha disparado. */
  loading: boolean
  /** La query con debounce ya aplicada (para resaltar coincidencias, etc.). */
  query: string
}

export function useLeadSearch(
  rawQuery: string,
  options: { limit?: number; enabled?: boolean } = {},
): LeadSearchResult {
  const { limit = 8, enabled = true } = options
  const query = useDebouncedValue(rawQuery.trim(), 280)
  const [results, setResults] = useState<InboxItem[]>([])
  const [loading, setLoading] = useState(false)
  // ¿El usuario está tecleando algo que aún no se ha buscado? → "buscando".
  const pending = enabled && rawQuery.trim() !== query

  useEffect(() => {
    if (!enabled || !query) {
      setResults([])
      setLoading(false)
      return
    }
    if (IS_PREVIEW) {
      setResults(filterLeadsByQuery(getDemoInbox(), query).slice(0, limit))
      setLoading(false)
      return
    }
    let cancelled = false
    const ctrl = new AbortController()
    setLoading(true)
    getInbox({ q: query }, { limit, signal: ctrl.signal })
      .then((r) => { if (!cancelled) setResults(r.items) })
      .catch((e) => {
        if (cancelled || (e as { code?: string })?.code === 'aborted') return
        setResults([])
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true; ctrl.abort() }
  }, [query, limit, enabled])

  return { results, loading: loading || pending, query }
}
